import { useSyncExternalStore } from "react";

import {
  findNeighbor,
  sortItems,
  type StepDirection,
  type StepOrigin,
} from "./layout";
import { type Deferred, deferred, NEVER, NO_SUBSCRIPTION } from "./loading";
import { itemsOverlapping, type TimelineTrackItem } from "./TimelineTrackItem";
import { sameWindow, type TimeWindow } from "./window";

/**
 * What the timeline sees for a window: the items in it, and whether they
 * are all here yet. Status words follow TanStack Query's vocabulary. `data`
 * is on every member so readers can count it without narrowing; it is empty
 * until `success`.
 */
export type TimelineDataResult<T> =
  | { status: "pending"; data: readonly TimelineTrackItem<T>[] }
  | { status: "success"; data: readonly TimelineTrackItem<T>[] }
  | { status: "error"; data: readonly TimelineTrackItem<T>[]; error: Error };

/**
 * Return every item that overlaps `range`, not only those starting in it.
 */
export type FetchItems<T> = (
  range: TimeWindow,
  signal: AbortSignal,
) => Promise<readonly TimelineTrackItem<T>[]>;

/**
 * Find the item before (`-1`) or after (`1`) `from` across the whole history,
 * loaded or not, in the timeline's order: start, then end, then id. `from` is
 * the selected item, or an instant (ms since the epoch) when nothing is
 * selected; from an instant, `1` finds the first item starting at or after it
 * and `-1` the last one starting before it. Resolve `null` when there is none;
 * the step or jump then does nothing. `signal` aborts when the answer is moot
 * (see `useNeighborSteps`).
 */
export type GetNeighbor<T> = (
  from: StepOrigin<T>,
  direction: StepDirection,
  signal: AbortSignal,
) => Promise<TimelineTrackItem<T> | null>;

export interface StreamerOptions {
  /**
   * The range to fetch when `window` is not covered by the loaded chunk.
   * Default: all of time, so one fetch serves every window.
   *
   * TODO(ENG-3298): a padded window around the view once backend session
   * aggregation makes per-range reads cheap; its response shape sets the unit.
   * Before chunks shrink, the streamer must also keep the last *settled*
   * chunk while the next one loads: today `requestData` for an uncovered
   * window replaces the only chunk with a pending one, so a synchronous
   * re-render at the old window (any `useSyncExternalStore` update is one)
   * would read pending and show the fallback, and the transition and the
   * sync render would abandon each other's requests. With all-of-time
   * chunks every window is covered after the first load, so it cannot
   * happen yet.
   *
   * This decision was made to simplify the code for a prototype.
   */
  chunkFor?: (window: TimeWindow) => TimeWindow;
}

export const ALL_TIME: TimeWindow = { start: -Infinity, end: Infinity };
const EMPTY: readonly never[] = Object.freeze([]);
/** Before anything was asked for, and while a chunk loads. */
const PENDING: TimelineDataResult<never> = Object.freeze({
  status: "pending",
  data: EMPTY,
});

/** A request in flight: its abort handle and the promise a suspended render waits on. */
interface Request {
  controller: AbortController;
  settled: Deferred;
}

const covers = (range: TimeWindow, window: TimeWindow) =>
  range.start <= window.start && range.end >= window.end;

/**
 * The module that hands the timeline the items for a window by fetching
 * and caching data when necessary.
 *
 * It is a Suspense data source: `requestData` is idempotent and may be
 * called during render, and `whenLoaded` is the promise a component throws
 * while the chunk it needs is loading. Starting a request never notifies
 * subscribers (a notify during render would re-render the subscriber
 * mid-render); only a settled chunk does.
 */
export class TimelineDataStreamer<T> {
  /** The unit of data we fetch and cache */
  #chunk: { range: TimeWindow; result: TimelineDataResult<T> } | undefined;
  #request: Request | null = null;
  #sorted:
    | {
        data: readonly TimelineTrackItem<T>[];
        sorted: readonly TimelineTrackItem<T>[];
      }
    | undefined;
  #disposed = false;
  readonly #listeners = new Set<() => void>();
  readonly #chunkFor: (window: TimeWindow) => TimeWindow;

  /**
   * The result of the previous ask, so the same window and chunk give the
   * same object. `useSyncExternalStore` requires it: a fresh object per read
   * would be a changed snapshot on every render.
   */
  #previousResult:
    | {
        window: TimeWindow;
        chunk: TimelineDataResult<T>;
        result: TimelineDataResult<T>;
      }
    | undefined;

  constructor(
    private readonly fetchItems: FetchItems<T>,
    { chunkFor = () => ALL_TIME }: StreamerOptions = {},
  ) {
    this.#chunkFor = chunkFor;
  }

  /**
   * Make sure the loaded chunk covers `window`; fetch a new chunk when it
   * does not. Safe to call during render: a no-op when the chunk covers the
   * window (loaded or loading), and silent otherwise.
   */
  requestData(window: TimeWindow): void {
    // Do nothing if the streamer is disposed
    if (this.#disposed) {
      return;
    }

    const chunk = this.#chunk;

    // Cache hit. The data streamer already has the requested data ready, or on its way.
    if (
      chunk &&
      chunk.result.status !== "error" &&
      covers(chunk.range, window)
    ) {
      return;
    }

    // Start a new fetch request; a chunk still loading for another window is abandoned.
    this.#abandon();
    const request: Request = {
      controller: new AbortController(),
      settled: deferred(),
    };
    this.#request = request;

    const range = this.#chunkFor(window);
    this.#chunk = { range, result: PENDING };

    this.fetchItems(range, request.controller.signal).then(
      (data) => this.#publish(request, range, { status: "success", data }),
      (error: unknown) =>
        this.#publish(request, range, {
          status: "error",
          data: EMPTY,
          error: error instanceof Error ? error : new Error(String(error)),
        }),
    );
  }

  /**
   * The items of the loaded chunk that fall in `window`, with the chunk's
   * status; pending before anything was asked for, and for a window the
   * chunk does not cover.
   *
   * Invariant: for a window that is pending — not covered, or covered by a
   * chunk still loading — this returns the one frozen `PENDING` object. A
   * request started during render must not change the snapshot React read
   * moments earlier, or React's post-render consistency check would force a
   * synchronous re-render and show the fallback. Tested.
   */
  getFetchResult(window: TimeWindow): TimelineDataResult<T> {
    const chunk = this.#chunk;

    if (chunk === undefined || !covers(chunk.range, window)) {
      return PENDING;
    }

    const previous = this.#previousResult;
    if (
      previous &&
      previous.chunk === chunk.result &&
      sameWindow(previous.window, window)
    ) {
      return previous.result;
    }

    const result: TimelineDataResult<T> =
      chunk.result.status === "success"
        ? {
            status: "success",
            data: itemsOverlapping(chunk.result.data, window),
          }
        : chunk.result;

    this.#previousResult = { window, chunk: chunk.result, result };
    return result;
  }

  /**
   * The neighbor of `from` (see `GetNeighbor`) as far as the loaded chunk can
   * tell, for steps when the page passes no `getNeighbor` and for whether a
   * step button is enabled. Returns the item; `null` when the chunk shows
   * there is none; `undefined` when it cannot say — nothing has loaded, the
   * chunk failed, `from` lies outside its range, or the answer could be an
   * item the chunk does not hold. With the default all-of-time chunk every
   * answer is known once it has loaded. Answering here means no caller needs
   * the whole chunk.
   */
  neighborOf(
    from: StepOrigin<T>,
    direction: StepDirection,
  ): TimelineTrackItem<T> | null | undefined {
    const chunk = this.#chunk;
    if (chunk === undefined || chunk.result.status !== "success") {
      return undefined;
    }
    const { range } = chunk;
    const at = typeof from === "number" ? from : from.startTime;
    if (at < range.start || at > range.end) {
      return undefined;
    }
    const found = findNeighbor(
      this.#sortedOf(chunk.result.data),
      from,
      direction,
    );
    // The chunk holds every item starting inside `range` (and some that start
    // before it and overlap it). So:
    // - a neighbor starting inside `range` is the true one: any item between
    //   it and `from` would also start inside `range`, and so be loaded;
    // - a neighbor starting outside `range`, or none found, is certain only
    //   when `range` is unbounded on that side; otherwise an unloaded item
    //   may come first.
    if (direction === 1) {
      if (found === null) {
        return range.end === Infinity ? null : undefined;
      }
      return found.startTime <= range.end ? found : undefined;
    }
    if (found === null) {
      return range.start === -Infinity ? null : undefined;
    }
    return found.startTime >= range.start ? found : undefined;
  }

  /**
   * Resolves when the request in flight settles (success, error or abandon);
   * already resolved when nothing is in flight; never, once disposed. What a
   * suspended component throws.
   */
  whenLoaded(): Promise<void> {
    if (this.#disposed) {
      return NEVER;
    }
    return this.#request?.settled.promise ?? Promise.resolve();
  }

  /** An arrow property so it is bound and identity-stable, as `useSyncExternalStore` requires. */
  readonly subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  /**
   * Abort the in-flight request; a result that arrives afterwards is dropped,
   * and later `requestData` calls are ignored rather than thrown on. That is
   * not defensive: React runs every effect cleanup of a commit before any
   * effect body, so when `getData` and the window change in the same render,
   * a render may still hold the streamer the `useStreamer` cleanup just
   * disposed. The next render uses the new one.
   */
  dispose(): void {
    this.#disposed = true;
    this.#abandon();
  }

  /**
   * The chunk's items in `compareItems` order for `findNeighbor`, re-sorted
   * only when the chunk's data changes.
   */
  #sortedOf(
    data: readonly TimelineTrackItem<T>[],
  ): readonly TimelineTrackItem<T>[] {
    if (this.#sorted?.data !== data) {
      this.#sorted = { data, sorted: sortItems(data) };
    }
    return this.#sorted.sorted;
  }

  /** Settle and abort the request in flight, if any, so a render suspended on it retries. */
  #abandon(): void {
    this.#request?.settled.resolve();
    this.#request?.controller.abort();
  }

  /** Record a settled chunk, unless its request was superseded or the streamer disposed. */
  #publish(
    request: Request,
    range: TimeWindow,
    result: TimelineDataResult<T>,
  ): void {
    request.settled.resolve();
    if (request.controller.signal.aborted) {
      return;
    }
    this.#chunk = { range, result };
    for (const listener of this.#listeners) {
      listener();
    }
  }
}

/**
 * The streamer's result for `window` as a React snapshot. A pure read: the
 * request is started by the component that suspends on it (`DataGate`), not
 * here. Pending until there is a streamer and a window to ask for.
 */
export function useTimelineData<T>(
  streamer: TimelineDataStreamer<T> | null,
  window: TimeWindow | null,
): TimelineDataResult<T> {
  return useSyncExternalStore(
    streamer?.subscribe ?? NO_SUBSCRIPTION,
    function getData(): TimelineDataResult<T> {
      return window === null || streamer === null
        ? PENDING
        : streamer.getFetchResult(window);
    },
  );
}
